import { MutableRefObject, useEffect, useRef } from 'react';

/**
 * Maps mouse wheel / trackpad input to horizontal scroll.
 * Prefer `target` when the scroll node is resolved after mount.
 */
export function useHorizontalScroll<T extends HTMLElement>(
  enabled = true,
  target?: T | null,
): MutableRefObject<T | null> {
  const elRef = useRef<T | null>(null);

  useEffect(() => {
    const el = target ?? elRef.current;
    if (!el || !enabled) return;

    const onWheel = (e: WheelEvent) => {
      if (el.scrollWidth <= el.clientWidth) return;

      // Trackpads often send deltaX; mice usually only deltaY — map either to X.
      const delta =
        Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY;
      if (delta === 0) return;

      const next = el.scrollLeft + delta;
      const max = el.scrollWidth - el.clientWidth;
      const clamped = Math.max(0, Math.min(max, next));
      if (clamped === el.scrollLeft) return;

      e.preventDefault();
      e.stopPropagation();
      el.scrollLeft = clamped;
    };

    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [enabled, target]);

  return elRef;
}